import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { PerformanceReviewInvitationContent } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { Stack } from "@skailer-com/uikit";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface PerformanceReviewInvitationFieldsProps extends PerformanceReviewInvitationContent {
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  subjectError?: string;
  bodyError?: string;
  variableOptions: ReturnType<
    typeof import("@/components/rich-editor/rich-editor.types.ts").getPerformanceReviewInvitationPlaceholderOptions
  >;
}

export const PerformanceReviewInvitationFields = ({
  invitation_subject,
  invitation_body,
  onSubjectChange,
  onBodyChange,
  subjectError,
  bodyError,
  variableOptions,
}: PerformanceReviewInvitationFieldsProps) => {
  const { t } = useTranslation();

  return (
    <Stack gap="md">
      <InputWithVariableInsert
        label={t("common:invitation_subject")}
        type="text"
        value={invitation_subject ?? ""}
        onChange={(event) => onSubjectChange(event.target.value)}
        error={subjectError}
        variableOptions={variableOptions}
      />
      <RichEditor
        value={invitation_body ?? ""}
        placeholder={t("core:enter_invitation_body_text") + "..."}
        onChange={onBodyChange}
        minHeight={100}
        error={bodyError}
        placeholderOptions={variableOptions}
      />
    </Stack>
  );
};

interface ControlledPerformanceReviewInvitationFieldsProps<T extends FieldValues> {
  control: Control<T>;
  subjectName: FieldPath<T>;
  bodyName: FieldPath<T>;
  subjectError?: string;
  bodyError?: string;
  variableOptions: PerformanceReviewInvitationFieldsProps["variableOptions"];
}

export const ControlledPerformanceReviewInvitationFields = <T extends FieldValues>({
  control,
  subjectName,
  bodyName,
  subjectError,
  bodyError,
  variableOptions,
}: ControlledPerformanceReviewInvitationFieldsProps<T>) => {
  const { field: subjectField } = useController({ control, name: subjectName });
  const { field: bodyField } = useController({ control, name: bodyName });

  return (
    <PerformanceReviewInvitationFields
      invitation_subject={(subjectField.value as string | null) ?? null}
      invitation_body={(bodyField.value as string | null) ?? null}
      onSubjectChange={subjectField.onChange}
      onBodyChange={bodyField.onChange}
      subjectError={subjectError}
      bodyError={bodyError}
      variableOptions={variableOptions}
    />
  );
};
